/* eslint-disable @typescript-eslint/no-explicit-any */
import useSWR, { SWRResponse } from 'swr';
import { getEnvironment } from '../environment';
import { Data, dataFetchers } from './data.fetcher';
import {
  DataFetcherFunction,
  DataFetcherReturn,
  HookOptions,
  HookOptionsWithFallback,
} from './data.types';
import { useMemo } from 'react';

/**
 * A hook to fetch data which is defined in the `data.fetcher.ts` file.
 *
 * This is a wrapper around the `useSWR` hook but with predefined data keys to fetch.
 *
 * @param key The data key to fetch (defined in `data.fetcher.ts`)
 * @param params The parameters for the data fetcher function (defined in `data.types.ts`)
 * @param config Optional configuration for the `useSWR` hook (passed through to `useSWR`)
 * @returns The data fetcher response (same as `useSWR`)
 *
 * @example
 * ```tsx
 * // usersByCustomer is a swr response (same when using `useSWR`)
 * const usersByCustomer = useData("USERS_BY_CUSTOMER", {customerUid: "xyz"});
 * ```
 */
/**
 * Typed overload for `useData` calls that provide `fallbackData`.
 *
 * This overload is selected when `config.fallbackData` is present.
 * In that case SWR always has an initial value for `data`, so the returned
 * response narrows `data` from `ReturnData | undefined` to `ReturnData`.
 *
 * Example:
 *
 * const { data } = useData('calls', {
 *   params: { types: [...] },
 *   config: { fallbackData: [] },
 * });
 *
 * // `data` is typed as `Call[]`, not `Call[] | undefined`.
 */
export function useData<DataKey extends keyof Data>(
  key: DataKey,
  options: HookOptionsWithFallback<Data[DataKey], DataFetcherReturn<DataKey>>,
): SWRResponse<DataFetcherReturn<DataKey>, any> & {
  data: DataFetcherReturn<DataKey>;
};

/**
 * Typed overload for regular `useData` calls without guaranteed fallback data.
 *
 * This overload is selected for the normal case where no `fallbackData` is
 * provided. SWR may not have loaded the data yet, so `data` keeps SWR's default
 * type of `ReturnData | undefined`.
 *
 * Example:
 *
 * const { data, isLoading } = useData('calls', {
 *   params: { types: [...] },
 * });
 *
 * // `data` may be undefined until the request has completed.
 */
export function useData<DataKey extends keyof Data>(
  key: DataKey,
  options: HookOptions<Data[DataKey], DataFetcherReturn<DataKey>>,
): SWRResponse<DataFetcherReturn<DataKey>, any>;

/**
 * Runtime implementation of `useData`.
 *
 * This is the single actual implementation behind the overloads above.
 * TypeScript overloads work by declaring one or more public call signatures,
 * followed by exactly one implementation signature. Only this function body is
 * emitted to JavaScript.
 *
 * Why there are three declarations:
 *
 * 1. The first overload models the special case with `fallbackData`.
 *    It tells TypeScript that `data` is definitely present.
 *
 * 2. The second overload models the normal SWR case.
 *    It preserves the default SWR behavior where `data` can be undefined.
 *
 * 3. The third function is the actual implementation.
 *    It accepts the broader shared option shape and contains the runtime logic.
 *
 * The casts inside the implementation are intentionally localised here.
 * `dataFetchers[key]` is a map lookup with a generic key. TypeScript sees this
 * internally as a union of all possible fetchers, even though the overloads
 * guarantee at the call-site that the selected `key`, `params`, and return type
 * belong together.
 *
 * `swrKey` is cast through `unknown` because SWR expects a precise tuple type:
 *
 * readonly [string, Params | undefined]
 *
 * Without the cast, TypeScript may widen `params` to the union of all possible
 * parameter types or even reduce it to `never` in generic indexed-access cases.
 * The public overloads keep the API type-safe; the implementation cast bridges
 * the limitation at the dynamic map boundary.
 */
export function useData<DataKey extends keyof Data>(
  key: DataKey,
  { params, config }: HookOptions<Data[DataKey], DataFetcherReturn<DataKey>>,
): SWRResponse<DataFetcherReturn<DataKey>, any> {
  const { axios } = getEnvironment();
  if (!axios) {
    throw new Error('useData hook can only be used when axios is defined');
  }

  const dataFetcher = dataFetchers[key];

  type Params = Data[DataKey];
  type ReturnData = DataFetcherReturn<DataKey>;
  type SwrKey = readonly [string, Params | undefined];

  const swrKey = useMemo(
    () => [dataFetcher.key, params],
    [dataFetcher.key, JSON.stringify(params)],
  );

  const fetcherFunction = dataFetcher.fetcher as unknown as DataFetcherFunction<Params, ReturnData>;

  return useSWR<ReturnData, any, SwrKey>(
    swrKey as unknown as SwrKey,
    ([, opt]) => fetcherFunction(opt as Params, { axios }),
    config,
  );
}
